import { type Node } from '@xyflow/react'
import { useState, useCallback } from 'react'

export interface PaneMenuState {
  isVisible: boolean
  top?: number
  left?: number
  right?: number
  bottom?: number
}

export interface NodeMenuState {
  id: string
  nodeType?: string
  top?: number
  left?: number
  right?: number
  bottom?: number
}

export interface SectionMenuState {
  isVisible: boolean
  nodeId?: string
  sectionId?: string
  sectionName?: string
  top?: number
  left?: number
  right?: number
  bottom?: number
}

export function useMenuManagement(
  ref: React.MutableRefObject<any>,
  getNode: (id: string) => Node | undefined
) {
  const [nodeMenu, setNodeMenu] = useState<NodeMenuState | null>(null)
  const [paneMenu, setPaneMenu] = useState<PaneMenuState | null>(null)
  const [sectionMenu, setSectionMenu] = useState<SectionMenuState>({
    isVisible: false,
  })

  // Handle node right-click
  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.preventDefault()
      event.stopPropagation()

      if (!ref.current) return

      const pane = ref.current.getBoundingClientRect()
      setNodeMenu({
        id: node.id,
        nodeType: node.type,
        top:
          event.clientY < pane.height - 200
            ? event.clientY - pane.top
            : undefined,
        left:
          event.clientX < pane.width - 200
            ? event.clientX - pane.left
            : undefined,
        right:
          event.clientX >= pane.width - 200
            ? pane.right - event.clientX
            : undefined,
        bottom:
          event.clientY >= pane.height - 200
            ? pane.bottom - event.clientY
            : undefined,
      })

      // Clear other menus
      setPaneMenu(null)
      setSectionMenu({ isVisible: false })
    },
    [ref]
  )

  // Handle pane right-click
  const onPaneContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()

      if (!ref.current) return

      // Check if event target is a node
      if (!(event.target as HTMLElement).closest('.react-flow__node')) {
        const pane = ref.current.getBoundingClientRect()
        const newPaneMenu: PaneMenuState = { isVisible: true }

        if (event.clientY < pane.height - 200) {
          newPaneMenu.top = event.clientY - pane.top
        } else {
          newPaneMenu.bottom = pane.bottom - event.clientY
        }

        if (event.clientX < pane.width - 200) {
          newPaneMenu.left = event.clientX - pane.left
        } else {
          newPaneMenu.right = pane.right - event.clientX
        }

        setPaneMenu(newPaneMenu)

        // Clear other menus
        setNodeMenu(null)
        setSectionMenu({ isVisible: false })
      }
    },
    [ref]
  )

  // Handle pane click to clear menus
  const onPaneClick = useCallback((event: React.MouseEvent) => {
    if (event.button === 0) {
      // Clear all menus
      setNodeMenu(null)
      setPaneMenu(null)
      setSectionMenu({ isVisible: false })

      // NOTE: User presence is now handled separately in the useNodeOperations hook
      // This ensures correct synchronization with YJS and prevents duplicate handlers
      // from causing conflicts. The onPaneClick handler from useNodeOperations 
      // will be responsible for clearing user presence.
    }
  }, [])

  // Handle movement to clear menus
  const onMove = useCallback(() => {
    setPaneMenu(null)
    setNodeMenu(null)
    setSectionMenu({ isVisible: false })
  }, [])

  // Handle section selection
  const handleSectionSelect = useCallback(
    (
      nodeId: string,
      sectionId: string,
      sectionName: string,
      event: { clientX: number; clientY: number }
    ) => {
      if (!ref.current) return;

      const pane = ref.current.getBoundingClientRect();
      if (!pane) return;

      const node = getNode(nodeId);
      if (!node) return;

      if (!pane) return;

      console.log('Retrieved node:', node)
      if (!node) {
        console.log('node is null, returning')
        return
      }

      const nodeElement = document.querySelector(`[data-id="${nodeId}"]`)
      console.log('Node element:', nodeElement)
      if (!nodeElement) {
        console.log('nodeElement is null, returning')
        return
      }

      const sectionElement = nodeElement.querySelector(
        `[data-section-id="${sectionId}"]`
      )!
      console.log('Section element:', sectionElement)
      if (!sectionElement) {
        console.log('sectionElement is null, returning')
        return
      }

      const sectionRect = sectionElement.getBoundingClientRect()
      console.log('Section bounding rectangle:', sectionRect)
      const paneRect = ref.current.getBoundingClientRect()
      console.log('Pane bounding rectangle:', paneRect)

      const newSectionMenu = {
        isVisible: true,
        nodeId,
        sectionId,
        top: sectionRect.top - paneRect.top,
        left: sectionRect.right,
      }
      console.log('New section menu state:', newSectionMenu)
      setSectionMenu(newSectionMenu);
    },
    [getNode, ref]
  )

  return {
    nodeMenu,
    setNodeMenu,
    paneMenu,
    setPaneMenu,
    sectionMenu,
    setSectionMenu,
    onNodeContextMenu,
    onPaneContextMenu,
    onPaneClick,
    onMove,
    handleSectionSelect,
  }
}

